import React, { useEffect, useState } from "react";

import { fetchChatAttachmentPreview } from "../../utils/chatAttachmentStore";
import { ALBUM_VISIBLE, albumMemberKey, albumRows } from "../../utils/chatAlbums";

/*
 * Альбом в сообщении: фото стоят сеткой в несколько рядов, как в Telegram и
 * в приложении. Видно до шести плиток; если фото больше — на последней «+N».
 * Нажатие на плитку открывает фото на весь экран, дальше их можно листать.
 */
export default function AlbumGrid({ messages = [], onOpen, onRetry, onTileMenu }) {
    const visible = messages.slice(0, ALBUM_VISIBLE);
    const hidden = messages.length - visible.length;
    const rows = albumRows(visible.length);

    let cursor = 0;

    return (
        <div className="chat-album-AO" data-count={messages.length}>
            {rows.map((count, rowIndex) => {
                const items = visible.slice(cursor, cursor + count);
                cursor += count;

                return (
                    <div key={rowIndex} className={`chat-album-AO__row is-${count}`}>
                        {items.map((item, index) => {
                            const last = rowIndex === rows.length - 1 && index === items.length - 1;
                            return (
                                <AlbumTile
                                    key={albumMemberKey(item)}
                                    message={item}
                                    // Крупным плиткам — чёткая копия, мелким хватит лёгкой.
                                    size={count <= 2 ? "medium" : "small"}
                                    more={last ? hidden : 0}
                                    onOpen={onOpen}
                                    onRetry={onRetry}
                                    onTileMenu={onTileMenu}
                                />
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

function AlbumTile({ message, size, more, onOpen, onRetry, onTileMenu }) {
    const localUrl = String(message?.__localUrl || "");
    const messageId = String(message?._id || "");
    const [url, setUrl] = useState(localUrl);
    const [failedLoad, setFailedLoad] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        // Своё фото с компьютера — сразу, без сервера.
        if (localUrl) {
            setUrl(localUrl);
            setFailedLoad(false);
            return undefined;
        }
        if (!messageId || messageId.startsWith("local:")) return undefined;

        let cancelled = false;
        let objectUrl = "";
        setFailedLoad(false);

        fetchChatAttachmentPreview({ message, size })
            .then((asset) => {
                if (cancelled || !asset?.blob) return;
                objectUrl = URL.createObjectURL(asset.blob);
                setUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setFailedLoad(true);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // message читаем только при смене номера: остальное в нём не влияет на фото.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt, localUrl, messageId, size]);

    const sending = message?.__sendStatus === "sending";
    const failed = message?.__sendStatus === "failed";
    const progress = Math.max(0, Math.min(100, Math.round(Number(message?.__uploadProgress || 0))));

    const press = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (failed) {
            onRetry?.(message);
            return;
        }
        if (failedLoad) {
            setAttempt((value) => value + 1);
            return;
        }
        if (url && !sending) onOpen?.(message);
    };

    return (
        <button
            type="button"
            className={["chat-album-AO__tile", failed ? "is-failed" : "", sending ? "is-sending" : ""].filter(Boolean).join(" ")}
            data-chat-message-id={messageId}
            data-album-tile="true"
            onClick={press}
            // Правая кнопка по фото — меню именно этого фото (удалить, переслать, скачать).
            onContextMenu={(event) => {
                if (!onTileMenu) return;
                event.preventDefault();
                event.stopPropagation();
                onTileMenu(message, event.clientX, event.clientY);
            }}
            title={failed ? "Не отправилось — нажмите, чтобы повторить" : more > 0 ? `Ещё фото: ${more}` : "Открыть фото"}
        >
            {url ? (
                <img src={url} alt="" loading="lazy" decoding="async" draggable={false} onError={() => { setUrl(""); setFailedLoad(true); }} />
            ) : (
                <span className="chat-album-AO__placeholder">{failedLoad ? "↻" : <i className="chat-album-AO__spinner" />}</span>
            )}

            {sending ? (
                <span className="chat-album-AO__cover is-sending">
                    <i className="chat-album-AO__spinner is-light" />
                    {progress > 0 && progress < 100 ? <small>{progress}%</small> : null}
                </span>
            ) : null}

            {failed ? (
                <span className="chat-album-AO__cover is-failed">
                    <b>!</b>
                    <small>Повторить</small>
                </span>
            ) : null}

            {more > 0 && !failed ? (
                <span className="chat-album-AO__cover is-more"><b>+{more}</b></span>
            ) : null}
        </button>
    );
}
